import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiFetch } from '../../lib/api.js'
import { GAME_DEFAULTS } from '../../lib/constants.js'

/**
 * Header unifié pour tous les écrans hôte — visible sur mobile uniquement (sm:hidden sur PC).
 *
 * Props :
 *   code          — code de la partie
 *   deckTitle     — nom du deck
 *   statut        — statut de la partie
 *   playerCount   — nombre de joueurs connectés
 *   maxPlayers    — capacité max
 *   startDisabled — désactive le bouton Démarrer (ex: 0 joueurs)
 *   onGameUpdate  — callback(type: string, data: object|null) appelé après chaque action
 */
export default function HostHeader({
  code, deckTitle, statut, playerCount = 0, maxPlayers = GAME_DEFAULTS.MAX_PLAYERS,
  startDisabled = false, onGameUpdate,
}) {
  const [loading, setLoading] = useState(null)

  async function callAction(type) {
    setLoading(type)
    try {
      const routes = {
        start:  { url: `/api/games/${code}/start`,  method: 'PUT' },
        pause:  { url: `/api/games/${code}/pause`,  method: 'PUT' },
        resume: { url: `/api/games/${code}/resume`, method: 'PUT' },
        end:    { url: `/api/games/${code}`,         method: 'DELETE' },
      }
      const { url, method } = routes[type]
      const data = await apiFetch(url, { method })
      onGameUpdate?.(type, data)
    } catch (e) {
      console.error('[HostHeader]', e.message)
    }
    setLoading(null)
  }

  async function handleEnd() {
    const msg = statut === 'waiting'
      ? 'Annuler cette partie ?'
      : 'Terminer cette partie ? Les joueurs seront redirigés.'
    if (!confirm(msg)) return
    callAction('end')
  }

  const navigate = useNavigate()
  const busy = loading !== null
  const showControls = ['waiting', 'playing', 'paused'].includes(statut)

  return (
    <header className="sm:hidden bg-gray-900 px-4 py-3 flex items-center justify-between gap-4 shrink-0 border-b border-white/10">

      {/* Retour home */}
      <button
        onClick={() => navigate('/home')}
        title="Retour à l'accueil"
        className="text-white/50 hover:text-white transition p-1 -ml-1 shrink-0 text-3xl leading-none"
      >
        ‹
      </button>

      {/* Infos partie */}
      <div className="min-w-0 flex-1">
        <h1 className="text-white font-semibold text-base leading-tight truncate">{deckTitle}</h1>
      </div>

      {/* Actions — masquées si partie terminée */}
      {showControls && <div className="flex items-center gap-0.5 shrink-0">

        {/* Play — Lancer / En cours / Reprendre */}
        <button
          onClick={() => {
            if (statut === 'waiting' && !busy && !startDisabled) callAction('start')
            else if (statut === 'paused' && !busy) callAction('resume')
          }}
          title={statut === 'playing' ? 'En cours' : statut === 'paused' ? 'Reprendre la course' : 'Lancer la course'}
          className={`flex flex-col items-center justify-center gap-0.5 py-1 px-2 rounded-lg transition ${
            statut === 'playing'
              ? 'bg-green-500/20 text-green-400 pointer-events-none'
              : busy || (statut === 'waiting' && startDisabled)
                ? 'text-white/20 pointer-events-none'
                : 'text-white/40 hover:text-green-400'
          }`}
        >
          {loading === 'start' || loading === 'resume' ? (
            <span className="text-xs w-4 h-4 flex items-center justify-center">…</span>
          ) : (
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <polygon points="5,3 19,12 5,21"/>
            </svg>
          )}
          <span className="text-[8px] font-medium leading-tight">
            {statut === 'playing' ? 'En cours' : statut === 'paused' ? 'Reprendre' : 'Lancer'}
          </span>
        </button>

        {/* Pause — masqué si waiting */}
        {statut !== 'waiting' && (
          <button
            onClick={() => statut === 'playing' && !busy && callAction('pause')}
            title={statut === 'paused' ? 'Course en pause' : 'Mettre la course en pause'}
            className={`flex flex-col items-center justify-center gap-0.5 py-1 px-2 rounded-lg transition ${
              statut === 'paused'
                ? 'bg-orange-500/20 text-orange-400 pointer-events-none'
                : busy
                  ? 'text-white/20 pointer-events-none'
                  : 'text-white/40 hover:text-orange-400'
            }`}
          >
            {loading === 'pause' ? (
              <span className="text-xs w-4 h-4 flex items-center justify-center">…</span>
            ) : (
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <rect x="6" y="4" width="4" height="16"/>
                <rect x="14" y="4" width="4" height="16"/>
              </svg>
            )}
            <span className="text-[8px] font-medium leading-tight">
              {statut === 'paused' ? 'En pause' : 'Pause'}
            </span>
          </button>
        )}

        {/* Stop / Annuler */}
        <button
          onClick={handleEnd}
          disabled={busy}
          title={statut === 'waiting' ? 'Annuler définitivement la course' : 'Terminer définitivement la course'}
          className="flex flex-col items-center justify-center gap-0.5 py-1 px-2 rounded-lg transition text-white/40 hover:text-red-400 disabled:opacity-40"
        >
          {loading === 'end' ? (
            <span className="text-xs w-4 h-4 flex items-center justify-center">…</span>
          ) : (
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <rect x="6" y="6" width="12" height="12"/>
            </svg>
          )}
          <span className="text-[8px] font-medium leading-tight">
            {statut === 'waiting' ? 'Annuler' : 'Stop'}
          </span>
        </button>
      </div>}
    </header>
  )
}
